import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { useMutation, useQuery } from "convex/react";
import { animate, motion, useMotionValue, useReducedMotion, type PanInfo } from "motion/react";
import { ChevronsRight, LockOpen, X } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { ENFORCEMENT_BREAK_MINUTES } from "../../lib/rules/enforcementBreak";
import {
  getDeviceTimeZone,
  projectRulesAfterBreak,
  projectRulesAfterStop,
  syncEnforcementToNative,
} from "../../lib/rules/enforcementSync";
import { canClaimNativeAppEnforcement } from "../../lib/rules/appBlockingHealth";
import { getEnforcingRules, getRulesOnBreakPause } from "../../lib/rules/ruleSchedule";
import { useEnforcementClock } from "../../lib/rules/useEnforcementClock";
import type { Rule, Target } from "../../lib/rules/ruleTypes";
import { triggerHoldCountdownHaptic, triggerSwipeProgressHaptic } from "../../lib/native/haptics";
import { useNativeAppBlockingStatus } from "../../lib/native/useNativeAppBlockingStatus";
import {
  clearAllOptimisticEnforcementRules,
  clearEnforcementProjection,
  mergeRulesWithOptimistic,
  setEnforcementProjection,
  useOptimisticEnforcementRules,
} from "./enforcementOptimistic";
import { subscribeSwipeUnlockHighlight } from "./swipeUnlockHighlight";
import { useSwipeUnlockChrome } from "../../lib/layout/SwipeUnlockChromeProvider";
import { StaticShaderFallback } from "./ActiveEnforcementBanner";
import { SwipeToUnlockTrackShader } from "./SwipeToUnlockTrackShader";
import {
  HOLD_TO_UNLOCK_SECONDS,
  buildSwipeHapticMilestones,
  getCrossedSwipeHapticMilestones,
  holdCountdownHapticPulseCount,
  holdCountdownRemaining,
  isSwipeUnlockThresholdReached,
  shouldFireHoldCountdownHaptic,
  swipeUnlockTrackTravel,
} from "./swipeUnlockLogic";

const HANDLE_SPRING = { type: "spring" as const, stiffness: 500, damping: 40, mass: 0.8 };

const TRACK_HIGHLIGHT_CSS = `
@keyframes swipe-track-pulse {
  0%, 100% { box-shadow: 0 0 0 0 oklch(72% 0.09 155 / 0); }
  50% { box-shadow: 0 0 0 4px oklch(72% 0.09 155 / 0.55), 0 0 20px oklch(72% 0.09 155 / 0.35); }
}
.swipe-track-highlight {
  animation: swipe-track-pulse 0.9s ease-in-out 2;
}
@media (prefers-reduced-motion: reduce) {
  .swipe-track-highlight {
    animation: none;
    box-shadow: 0 0 0 3px oklch(72% 0.09 155 / 0.5);
  }
}
`;

const rulesListRef = queryRef<{ anonymousDeviceId: string }, Rule[]>("rules:list");
const targetsListRef = queryRef<{ anonymousDeviceId: string }, Target[]>("targets:list");
const stopAllEnforcementRef = mutationRef<
  { anonymousDeviceId: string; nowMs: number; timeZone: string },
  { stoppedCount: number }
>("rules:stopAllEnforcement");
const pauseAllEnforcementForBreakRef = mutationRef<
  { anonymousDeviceId: string; durationMinutes: number; nowMs: number; timeZone: string },
  { pausedCount: number }
>("rules:pauseAllEnforcementForBreak");

/**
 * Global "slide to unlock" control — mounted once at the app root. Visible
 * whenever at least one rule (scheduled or quick session) is currently
 * enforcing; sliding the handle to the end and holding the confirm button for
 * HOLD_TO_UNLOCK_SECONDS stops everything via `rules:stopAllEnforcement`.
 */
export function SwipeToUnlockBar() {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const anonymousDeviceId = useDeviceId();
  const rules = useQuery(rulesListRef, { anonymousDeviceId });
  const targets = useQuery(targetsListRef, { anonymousDeviceId });
  const stopAllEnforcement = useMutation(stopAllEnforcementRef);
  const pauseAllEnforcementForBreak = useMutation(pauseAllEnforcementForBreakRef);
  const optimisticEnforcementRules = useOptimisticEnforcementRules();
  const mergedRules = useMemo(
    () => mergeRulesWithOptimistic(rules ?? []),
    [rules, optimisticEnforcementRules],
  );
  const { now } = useEnforcementClock(mergedRules);
  const { appBlocking } = useNativeAppBlockingStatus();
  const isNativePlatform = Capacitor.isNativePlatform();
  const canClaimEnforcement = canClaimNativeAppEnforcement(appBlocking, { isNativePlatform });

  useEffect(() => {
    if (rules === undefined) return;
    const scheduleNow = new Date(now);
    const hasEnforcement =
      getEnforcingRules(mergedRules, scheduleNow).length > 0 ||
      getRulesOnBreakPause(mergedRules, scheduleNow).length > 0;
    if (!hasEnforcement) {
      clearEnforcementProjection();
    }
  }, [mergedRules, rules, now]);

  const enforcingRules = useMemo(
    () => (rules === undefined ? [] : getEnforcingRules(mergedRules, now)),
    [rules, mergedRules, now],
  );
  const breakPauseRules = useMemo(
    () => (rules === undefined ? [] : getRulesOnBreakPause(mergedRules, now)),
    [rules, mergedRules, now],
  );
  const onBreakPause = breakPauseRules.length > 0;
  const visible =
    rules !== undefined &&
    canClaimEnforcement &&
    (enforcingRules.length > 0 || onBreakPause);

  const { setEnforcementActive, scrollHidden } = useSwipeUnlockChrome();

  useEffect(() => {
    setEnforcementActive(visible);
  }, [setEnforcementActive, visible]);

  const trackRef = useRef<HTMLDivElement>(null);

  const applyLocalEnforcementRelease = useCallback(
    async (projectedRules: Rule[]) => {
      clearAllOptimisticEnforcementRules();
      setEnforcementProjection(projectedRules);
      const scheduleNow = new Date();
      await syncEnforcementToNative(projectedRules, targets ?? [], scheduleNow);
    },
    [targets],
  );

  const handleRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [confirming, setConfirming] = useState(false);
  const [holding, setHolding] = useState(false);
  const [remaining, setRemaining] = useState(HOLD_TO_UNLOCK_SECONDS);
  const holdStartRef = useRef<number | null>(null);
  const holdFrameRef = useRef<number | null>(null);
  const lastHapticRemainingRef = useRef<number | null>(null);
  const lastSwipeOffsetPxRef = useRef(0);
  const swipeHapticMilestonesRef = useRef<number[]>([]);
  const stoppingRef = useRef(false);
  const breakStartingRef = useRef(false);
  const [trackHighlighted, setTrackHighlighted] = useState(false);
  const trackHighlightTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return subscribeSwipeUnlockHighlight(() => {
      setTrackHighlighted(true);
      if (trackHighlightTimerRef.current !== null) {
        window.clearTimeout(trackHighlightTimerRef.current);
      }
      trackHighlightTimerRef.current = window.setTimeout(() => {
        setTrackHighlighted(false);
        trackHighlightTimerRef.current = null;
      }, prefersReducedMotion ? 1200 : 1800);
    });
  }, [prefersReducedMotion]);

  useEffect(() => {
    return () => {
      if (trackHighlightTimerRef.current !== null) {
        window.clearTimeout(trackHighlightTimerRef.current);
      }
    };
  }, []);

  const resetHandle = useCallback(() => {
    if (prefersReducedMotion) {
      x.set(0);
      return;
    }
    void animate(x, 0, HANDLE_SPRING);
  }, [prefersReducedMotion, x]);

  const cancelHold = useCallback(() => {
    if (holdFrameRef.current !== null) {
      cancelAnimationFrame(holdFrameRef.current);
      holdFrameRef.current = null;
    }
    holdStartRef.current = null;
    lastHapticRemainingRef.current = null;
    setHolding(false);
    setRemaining(HOLD_TO_UNLOCK_SECONDS);
  }, []);

  const closeOverlay = useCallback(() => {
    cancelHold();
    setConfirming(false);
    resetHandle();
  }, [cancelHold, resetHandle]);

  const resetSwipeHapticState = useCallback(() => {
    lastSwipeOffsetPxRef.current = 0;
    swipeHapticMilestonesRef.current = [];
  }, []);

  const handleSwipePointerDown = useCallback(() => {
    if (prefersReducedMotion) return;

    const track = trackRef.current;
    const handle = handleRef.current;
    if (!track || !handle) return;

    const travel = swipeUnlockTrackTravel(track.offsetWidth, handle.offsetWidth);
    swipeHapticMilestonesRef.current = buildSwipeHapticMilestones(travel);
    lastSwipeOffsetPxRef.current = x.get();
    triggerSwipeProgressHaptic();
  }, [prefersReducedMotion, x]);

  const maybeFireSwipeProgressHaptic = useCallback(
    (offsetX: number) => {
      if (prefersReducedMotion) return;

      const track = trackRef.current;
      const handle = handleRef.current;
      if (!track || !handle) return;

      const travel = swipeUnlockTrackTravel(track.offsetWidth, handle.offsetWidth);
      const prevPx = Math.min(Math.max(0, lastSwipeOffsetPxRef.current), travel);
      const nextPx = Math.min(Math.max(0, offsetX), travel);

      const crossed = getCrossedSwipeHapticMilestones(
        prevPx,
        nextPx,
        swipeHapticMilestonesRef.current,
      );

      for (let i = 0; i < crossed.length; i += 1) {
        triggerSwipeProgressHaptic();
      }

      lastSwipeOffsetPxRef.current = offsetX;
    },
    [prefersReducedMotion],
  );

  const maybeFireCountdownHaptic = useCallback((secondsLeft: number) => {
    if (!shouldFireHoldCountdownHaptic(lastHapticRemainingRef.current, secondsLeft)) return;
    lastHapticRemainingRef.current = secondsLeft;
    triggerHoldCountdownHaptic(holdCountdownHapticPulseCount(secondsLeft));
  }, []);

  // If enforcement stops for any other reason (natural expiry, edited elsewhere)
  // while the overlay is open, close it and reset the handle.
  useEffect(() => {
    if (!visible) closeOverlay();
  }, [visible, closeOverlay]);

  useEffect(() => cancelHold, [cancelHold]);

  const handleDragEnd = useCallback(
    (_event: PointerEvent, info: PanInfo) => {
      resetSwipeHapticState();
      const track = trackRef.current;
      const handle = handleRef.current;
      if (track && handle && isSwipeUnlockThresholdReached(info.offset.x, track.offsetWidth, handle.offsetWidth)) {
        setConfirming(true);
        return;
      }
      resetHandle();
    },
    [resetHandle, resetSwipeHapticState],
  );

  const tickHold = useCallback(() => {
    if (holdStartRef.current === null) return;
    const elapsedMs = performance.now() - holdStartRef.current;
    const secondsLeft = holdCountdownRemaining(elapsedMs);
    setRemaining(secondsLeft);
    maybeFireCountdownHaptic(secondsLeft);

    if (secondsLeft > 0) {
      holdFrameRef.current = requestAnimationFrame(tickHold);
      return;
    }

    holdFrameRef.current = null;
    holdStartRef.current = null;
    lastHapticRemainingRef.current = null;
    setHolding(false);
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    const nowMs = Date.now();
    const timeZone = getDeviceTimeZone();
    const projectedRules = projectRulesAfterStop(mergedRules, nowMs, timeZone);
    void applyLocalEnforcementRelease(projectedRules)
      .then(() => stopAllEnforcement({ anonymousDeviceId, nowMs, timeZone }))
      .catch((error) => {
        console.error("[swipe-to-unlock] stopAllEnforcement failed", error);
        clearEnforcementProjection();
      })
      .finally(() => {
        stoppingRef.current = false;
        setConfirming(false);
        resetHandle();
      });
  }, [anonymousDeviceId, applyLocalEnforcementRelease, maybeFireCountdownHaptic, mergedRules, resetHandle, stopAllEnforcement]);

  const startHold = useCallback(() => {
    holdStartRef.current = performance.now();
    lastHapticRemainingRef.current = null;
    setRemaining(HOLD_TO_UNLOCK_SECONDS);
    setHolding(true);
    maybeFireCountdownHaptic(HOLD_TO_UNLOCK_SECONDS);
    holdFrameRef.current = requestAnimationFrame(tickHold);
  }, [maybeFireCountdownHaptic, tickHold]);

  const handleTakeBreak = useCallback(() => {
    if (breakStartingRef.current) return;
    breakStartingRef.current = true;
    const nowMs = Date.now();
    const timeZone = getDeviceTimeZone();
    const projectedRules = projectRulesAfterBreak(
      mergedRules,
      nowMs,
      timeZone,
      ENFORCEMENT_BREAK_MINUTES,
    );
    void applyLocalEnforcementRelease(projectedRules)
      .then(() =>
        pauseAllEnforcementForBreak({
          anonymousDeviceId,
          durationMinutes: ENFORCEMENT_BREAK_MINUTES,
          nowMs,
          timeZone,
        }),
      )
      .catch((error) => {
        console.error("[swipe-to-unlock] pauseAllEnforcementForBreak failed", error);
        clearEnforcementProjection();
      })
      .finally(() => {
        breakStartingRef.current = false;
        closeOverlay();
      });
  }, [anonymousDeviceId, applyLocalEnforcementRelease, closeOverlay, mergedRules, pauseAllEnforcementForBreak]);

  if (!visible) return null;

  const shaderVariant = onBreakPause && enforcingRules.length === 0 ? "pause" : "enforcement";

  return (
    <>
      <style>{TRACK_HIGHLIGHT_CSS}</style>
      <motion.div
        className="pointer-events-none fixed inset-x-0 bottom-[calc(max(12px,env(safe-area-inset-bottom))+72px)] z-[40] flex justify-center px-4"
        aria-hidden={scrollHidden}
        animate={{
          y: scrollHidden ? 72 : 0,
          opacity: scrollHidden ? 0 : 1,
        }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { type: "tween", duration: 0.28, ease: [0.4, 0, 0.2, 1] }
        }
      >
        <div
          ref={trackRef}
          className={`liquid-glass-panel pointer-events-auto relative h-14 w-full max-w-[420px] overflow-hidden rounded-full${trackHighlighted ? " swipe-track-highlight" : ""}${scrollHidden ? " pointer-events-none" : ""}`}
        >
          {prefersReducedMotion ? (
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
              <StaticShaderFallback variant={shaderVariant} />
            </div>
          ) : (
            <SwipeToUnlockTrackShader
              variant={shaderVariant}
              animate={!prefersReducedMotion}
            />
          )}
          <div
            className={
              shaderVariant === "pause"
                ? "pointer-events-none absolute inset-0 rounded-[inherit] bg-[oklch(22%_0.04_62/0.58)] backdrop-blur-[2px]"
                : "pointer-events-none absolute inset-0 rounded-[inherit] bg-[oklch(12%_0.015_160/0.58)] backdrop-blur-[2px]"
            }
            aria-hidden
          />
          <span
            className={
              shaderVariant === "pause"
                ? "pointer-events-none absolute inset-0 flex items-center justify-center px-16 text-center text-sm font-semibold text-[oklch(90%_0.06_68)]"
                : "pointer-events-none absolute inset-0 flex items-center justify-center px-16 text-center text-sm font-semibold text-[oklch(90%_0.02_150)]"
            }
          >
            {t.rules.swipeUnlock.trackLabel}
          </span>
          <motion.div
            ref={handleRef}
            role="button"
            tabIndex={0}
            aria-label={t.rules.swipeUnlock.trackAria}
            drag="x"
            dragConstraints={trackRef}
            dragElastic={0}
            dragMomentum={false}
            onPointerDown={handleSwipePointerDown}
            onDrag={(_event, info) => maybeFireSwipeProgressHaptic(info.offset.x)}
            onDragEnd={handleDragEnd}
            style={{ x, touchAction: "none" }}
            className={
              shaderVariant === "pause"
                ? "absolute left-1 top-1 grid h-12 w-12 place-items-center rounded-full bg-[oklch(78%_0.12_68)] text-[oklch(18%_0.03_62)] shadow-lg active:cursor-grabbing"
                : "absolute left-1 top-1 grid h-12 w-12 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)] shadow-lg active:cursor-grabbing"
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setConfirming(true);
              }
            }}
          >
            <ChevronsRight size={20} aria-hidden />
          </motion.div>
        </div>
      </motion.div>

      {confirming
        ? createPortal(
            <div
              className="fixed inset-0 z-[90] flex flex-col bg-[oklch(8%_0.01_160/0.94)] px-6 text-center backdrop-blur-sm"
              role="dialog"
              aria-modal="true"
              aria-label={t.rules.swipeUnlock.overlayTitle}
            >
              <button
                type="button"
                onClick={closeOverlay}
                className="icon-button absolute right-4 top-[max(16px,env(safe-area-inset-top))]"
                style={{ minWidth: 40, minHeight: 40, border: "none" }}
                aria-label={t.common.close}
              >
                <X size={18} />
              </button>

              <div
                className="flex flex-1 flex-col items-center justify-end gap-3 pb-4 pt-[max(56px,env(safe-area-inset-top))]"
                aria-live="polite"
                aria-atomic="true"
              >
                {holding ? (
                  <>
                    <p className="max-w-xs text-center text-lg font-semibold text-[oklch(82%_0.04_155)]">
                      {t.rules.swipeUnlock.holdHint}
                    </p>
                    <p
                      className="mono select-none text-[min(28vw,120px)] font-bold tabular-nums leading-none text-[oklch(88%_0.07_155)]"
                      aria-hidden
                    >
                      {remaining}
                    </p>
                  </>
                ) : (
                  <span className="h-[min(28vw,120px)]" aria-hidden />
                )}
              </div>

              <div className="flex shrink-0 flex-col items-center gap-5 pb-[max(24px,env(safe-area-inset-bottom))]">
                {!holding ? (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
                      {t.rules.swipeUnlock.overlayTitle}
                    </p>
                    <p className="max-w-xs text-sm leading-relaxed text-[oklch(70%_0.025_155)]">
                      {t.rules.swipeUnlock.overlayBody}
                    </p>
                  </div>
                ) : null}

                <button
                  type="button"
                  aria-label={t.rules.swipeUnlock.holdAria}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    startHold();
                  }}
                  onPointerUp={cancelHold}
                  onPointerLeave={cancelHold}
                  onPointerCancel={cancelHold}
                  className={`liquid-glass-icon-ring grid h-28 w-28 select-none place-items-center rounded-full text-[oklch(88%_0.07_155)] transition-transform ${holding ? "scale-95" : ""}`}
                  style={{ touchAction: "none" }}
                >
                  <LockOpen size={36} aria-hidden />
                </button>

                <button
                  type="button"
                  onClick={handleTakeBreak}
                  className="secondary-action w-full max-w-xs justify-center px-4 py-3 text-sm font-semibold"
                >
                  {formatTemplate(t.rules.swipeUnlock.takeBreak, {
                    minutes: ENFORCEMENT_BREAK_MINUTES,
                  })}
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
